import { Box, Checkbox, Drawer, FormControl, InputAdornment, List, ListItem, ListItemButton, ListItemText, NativeSelect, Stack, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useContext, useEffect, useState } from 'react';
import { isAdminUser, isTrainerUser } from '@/utils/user';
import { AppContext } from '@/contexts/app.context';
import { DeckDTO } from '@/generated';
import { useListUserDecksQuery } from '@/hooks/query-hooks';
import { enqueueSnackbar } from 'notistack';
import { useAssignDeckToUser } from '@/hooks/deck-mutation.hooks';

export interface AddDeckProps {
  show: boolean;
  userId: string;
  setShow: (show: boolean) => void;
}

interface FilterProps {
  userId?: string | undefined;
  name?: string;
}

export const filter = (decks: Array<DeckDTO>, props: FilterProps) => {
  return decks
    .filter((s) => (props.name ? s.name.toLowerCase().includes(props.name.toLowerCase()) : true))
    .filter((s) => ((props.userId ?? '').trim() !== '' ? s.creatorId === props.userId : true));
};

export const AddDeckDrawer = (props: AddDeckProps) => {
  const [allDecks, setAllDecks] = useState<Array<DeckDTO>>([]);
  const [filteredDecks, setFilteredDecks] = useState<Array<DeckDTO>>();
  const [filterProps, setFilterProps] = useState<FilterProps>({});
  const [setUsers, setSetUsers] = useState<{ id: string; name: string }[]>([]);
  const [addUserToEditors, setAddUserToEditors] = useState<boolean>(false);
  const [sendEmailToUser, setSendEmailToUser] = useState<boolean>(false);
  const context = useContext(AppContext);
  const user = context.state?.userProfile;
  const { decks } = useListUserDecksQuery(props.show);
  const assignDeckMutation = useAssignDeckToUser();

  useEffect(() => {
    const nonUniqueUsers = decks?.map((s) => ({ id: s.creatorId, name: s.creatorName }));
    const uniqueUsers = Array.from(new Set(nonUniqueUsers?.map((nuu) => nuu.id))).map((id) => {
      return nonUniqueUsers?.find((u) => u.id === id);
    }) as { id: string; name: string }[];

    setSetUsers(uniqueUsers);
    setAllDecks(decks ?? []);
    setFilteredDecks(decks);
  }, [decks]);

  useEffect(() => {
    console.log(allDecks, filterProps, filter(allDecks, filterProps));
    setFilteredDecks(filter(allDecks, filterProps));
  }, [filterProps, allDecks]);

  useEffect(() => {
    if (user) setAddUserToEditors(isTrainerUser(user) ?? false);
  }, [user]);

  return (
    <>
      <Drawer anchor="right" open={props.show} onClose={() => props.setShow(false)}>
        <Box sx={{ mx: 2, py: 2 }}>
          <Typography variant="h5">Assign Deck to User</Typography>
          {(isAdminUser(user) || isTrainerUser(user)) && (
            <Stack direction={'row'} sx={{ gap: 1, mt: 3, mx: 2 }}>
              <Checkbox
                checked={addUserToEditors}
                onChange={() => {
                  setAddUserToEditors(!addUserToEditors);
                }}
              />
              <Typography>Assign to Editors</Typography>
            </Stack>
          )}
          {(isAdminUser(user) || isTrainerUser(user)) && (
            <Stack direction={'row'} sx={{ gap: 1, mt: 3, mx: 2 }}>
              <Checkbox
                checked={sendEmailToUser}
                onChange={() => {
                  setSendEmailToUser(!sendEmailToUser);
                }}
              />
              <Typography>Send Email to User</Typography>
            </Stack>
          )}
          <Stack gap={2} sx={{ mt: 3 }}>
            <TextField
              label="Set Name"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              onChange={(e) => {
                setFilterProps({ ...filterProps, name: e.target.value });
              }}
            />
            <Stack direction={'row'} sx={{ gap: 3 }}>
              <Typography>filter by </Typography>
              <FormControl>
                <NativeSelect
                  value={filterProps.userId}
                  placeholder="Not Set"
                  style={{ zIndex: 1, maxWidth: 200 }}
                  id="input-group-dropdown-user-name"
                  onChange={(e) => {
                    setFilterProps({ ...filterProps, userId: e.target.value });
                  }}
                >
                  <option value={''}>None</option>
                  {setUsers?.map((n) => (
                    <option value={n.id}>{n.name}</option>
                  ))}
                </NativeSelect>
              </FormControl>
            </Stack>
          </Stack>

          <List>
            {filteredDecks?.map((s: DeckDTO) => (
              <ListItem key={'Decks'} disablePadding>
                <ListItemButton
                  onClick={async () => {
                    if (s.id) await assignDeckMutation.mutateAsync({ deckId: s.id, userId: props.userId, addUserToEditors, sendEmail: sendEmailToUser });
                    props.setShow(false);
                    enqueueSnackbar('Deck successfully assigned to user', { variant: 'success' });
                  }}
                >
                  <ListItemText primary={s.name} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
    </>
  );
};
